import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SearchBar } from '../components/ui/SearchBar';
import { CategoryTile } from '../components/ui/CategoryTile';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { searchDevices } from '../services/devices';
import { listStories } from '../services/stories';
import type { Category } from '../types/models';

const CATEGORIES: { category: Category; label: string }[] = [
  { category: 'hifi', label: 'Hi-Fi' },
  { category: 'smartphone', label: 'Smartphones' },
  { category: 'laptop', label: 'Laptops' },
  { category: 'camera', label: 'Cameras' },
  { category: 'smart-home', label: 'Smart Home' },
  { category: 'gaming', label: 'Gaming' },
  { category: 'car', label: 'Cars' },
  { category: 'ebike', label: 'E-Bikes' },
  { category: 'watch', label: 'Watches' },
  { category: 'other', label: 'Other' },
];

export function HomePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const q = params.get('q') || '';
  const category = (params.get('category') as Category | null) || undefined;

  const stories = useQuery({
    queryKey: ['stories'],
    queryFn: listStories,
  });

  const searchResults = useQuery({
    queryKey: ['devices', 'search', q, category],
    queryFn: () => searchDevices(q, category),
    enabled: q.length > 0 || !!category,
  });

  const handleSearch = (value: string) => {
    const next = new URLSearchParams();
    if (value) next.set('q', value);
    if (category) next.set('category', category);
    navigate(`/?${next.toString()}`);
  };

  return (
    <div className="flex flex-col gap-10">
      <section className="flex flex-col items-center gap-4 py-6">
        <h1 className="text-4xl sm:text-5xl font-bold text-center">
          Find a replacement battery <span className="text-lime-400">in seconds</span>
        </h1>
        <p className="text-neutral-400 text-center max-w-xl">
          Search any phone, laptop, camera or e-bike. We rank vendors by price and stock so you
          pick the best swap.
        </p>
        <div className="w-full max-w-2xl">
          <SearchBar initialValue={q} onSubmit={handleSearch} />
        </div>
      </section>

      {(q || category) && (
        <section>
          <h2 className="mb-4">Search results</h2>
          {searchResults.isLoading && <Spinner />}
          {searchResults.isError && (
            <Card className="text-accent-red">
              Could not load results. Please try again.
            </Card>
          )}
          {searchResults.isSuccess && searchResults.data.length === 0 && (
            <Card className="text-neutral-400">No devices match your search.</Card>
          )}
          {searchResults.isSuccess && searchResults.data.length > 0 && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {searchResults.data.map((d) => (
                <li key={d._id}>
                  <a href={`/devices/${d._id}`} className="card block hover:border-lime-500">
                    <div className="text-xs uppercase text-neutral-400">{d.brand}</div>
                    <div className="text-lg font-semibold">{d.name}</div>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section>
        <h2 className="mb-4">Browse by category</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {CATEGORIES.map((c) => (
            <CategoryTile key={c.category} category={c.category} label={c.label} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-4">Featured stories</h2>
        {stories.isLoading && <Spinner />}
        {stories.isError && (
          <Card className="text-accent-red">Could not load stories right now.</Card>
        )}
        {stories.isSuccess && stories.data.length === 0 && (
          <Card className="text-neutral-400">No stories published yet.</Card>
        )}
        {stories.isSuccess && stories.data.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {stories.data.map((s) => (
              <Card key={s._id}>
                <div className="text-xs uppercase text-neutral-400 mb-1">Story</div>
                <div className="text-lg font-semibold">{s.title}</div>
                <p className="text-sm text-neutral-400 mt-2">{s.excerpt}</p>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
